<template>
  <div class="payment-block">
    <div v-if="localError" class="pay-error">{{ localError }}</div>

    <div class="payment-card">
      <div v-if="showMethodToggle" class="pay-method-toggle">
        <label v-for="opt in methodOptions" :key="opt.value" :class="{ active: method === opt.value }">
          <input type="radio" :value="opt.value" v-model="method" @change="onMethodChange" /> {{ opt.label }}
        </label>
      </div>

      <div v-show="method === 'card'">
        <div v-show="expressAvailable">
          <div id="lp-express-checkout"></div>
          <div class="pay-express-divider"><span>or pay with card</span></div>
        </div>
        <div id="lp-card-element"></div>
      </div>

      <div v-show="method === 'affirm'">
        <div id="lp-affirm-element"></div>
        <p class="pay-note">After clicking “Pay”, you'll be redirected to Affirm to complete your purchase securely.</p>
      </div>

      <div v-show="method === 'offline'">
        <p class="pay-note">Your spot is reserved immediately. You'll receive payment instructions (check or purchase order) after registration.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { loadStripe } from '@stripe/stripe-js'
import { mountExpressCheckout } from '../../../../global-declare-components/helpers/stripeExpressCheckout'

export default {
  name: 'lp-payment',
  data() {
    return {
      stripe: null,
      elements: null,
      cardElement: null,
      affirmElement: null,
      expressCheckout: null,
      expressAvailable: false,
      method: 'card',
      loading: false,
      localError: '',
    }
  },
  computed: {
    ...mapState('registration', ['paymentInfo', 'settings', 'basket']),
    ...mapGetters('registration', ['currencySymbol', 'totalPrice']),
    total() { return this.totalPrice },
    methodOptions() {
      const opts = [{ value: 'card', label: 'Credit card' }]
      if (this.settings.instalment_payments && this.total >= 50) {
        opts.push({ value: 'affirm', label: 'Affirm — pay over time' })
      }
      if (this.settings.offline_payments) {
        opts.push({ value: 'offline', label: 'Pay by check or purchase order' })
      }
      return opts
    },
    showMethodToggle() {
      return this.methodOptions.length > 1
    },
  },
  watch: {
    loading(v) { this.$emit('loading-change', v) },
    method(v) { this.$emit('method-change', v) },
  },
  async mounted() {
    this.$emit('method-change', this.method)
    await this.initStripe()
  },
  beforeDestroy() {
    this.destroyWallets()

    if (this.cardElement) { this.cardElement.destroy() }
    if (this.affirmElement) { this.affirmElement.destroy() }
  },
  methods: {
    ...mapActions('registration', ['preparePayment', 'confirmPayment']),
    currency() {
      return (this.settings.currency || 'usd').toLowerCase()
    },
    async initStripe() {
      const info = this.paymentInfo
      if (!info || !info.stripe || !info.stripe.public_key) return

      // Gate on stripe_account alone: the PaymentIntent is created on the
      // connected account whenever one exists, so also requiring `destination`
      // (the external bank account) meant an organization without a payout
      // account initialised Stripe.js on the platform and got
      // "No such payment_intent".
      const opts = {}
      if (info.stripe.stripe_account) {
        opts.stripeAccount = info.stripe.stripe_account
      }
      this.stripe = await loadStripe(info.stripe.public_key, Object.keys(opts).length ? opts : undefined)

      if (!info.client_secret) return

      this.elements = this.stripe.elements({ clientSecret: info.client_secret, loader: 'always' })
      await this.mountCard()
      this.mountWallets(info.client_secret)
    },
    async mountCard() {
      if (this.cardElement) { this.cardElement.unmount(); this.cardElement = null }
      this.cardElement = this.elements.create('payment', {
        fields: { billingDetails: { name: 'never', email: 'never' } },
        // Wallets live in the Express Checkout Element above the form, so the
        // Payment Element must not offer them again as tabs.
        wallets: { applePay: 'never', googlePay: 'never' },
      })
      this.cardElement.mount('#lp-card-element')
    },
    async mountAffirm() {
      if (this.affirmElement) { this.affirmElement.unmount() }
      this.affirmElement = this.elements.create('affirmMessage', {
        amount: Math.round(this.total * 100),
        country: 'US',
        currency: this.currency().toUpperCase(),
      })
      this.affirmElement.mount('#lp-affirm-element')
    },
    mountWallets(clientSecret) {
      this.destroyWallets()

      this.expressCheckout = mountExpressCheckout({
        stripe: this.stripe,
        elements: this.elements,
        selector: '#lp-express-checkout',
        clientSecret,
        returnUrl: this.paymentInfo.redirect_url,
        onConfirm: () => this.finish(),
        onError: (message) => { this.localError = message },
        onAvailable: (available) => { this.expressAvailable = available },
        onBusy: (busy) => { this.loading = busy },
      })
    },
    destroyWallets() {
      this.expressAvailable = false

      if (this.expressCheckout) {
        this.expressCheckout.destroy()
        this.expressCheckout = null
      }
    },
    async onMethodChange() {
      await this.reprepare('Could not switch payment method. Please try again.')
    },
    // Called by the parent when the order total changes on this step (coupon or
    // donation from the rail). The PaymentIntent was created for the total at
    // prepare time, so without re-preparing the card is charged the old amount
    // while the summary and the Pay button already show the new one.
    async refresh() {
      await this.reprepare('Could not update the payment amount. Please try again.')
    },
    async reprepare(errorMessage) {
      this.localError = ''
      // Disable the Pay button while re-preparing — the `loading` watcher emits
      // `loading-change`, which the parent binds to the footer button's :disabled.
      this.loading = true
      try {
        // The Elements group is rebuilt below against a fresh client secret, so
        // the wallet element bound to the old group has to go first.
        this.destroyWallets()

        if (this.method === 'offline') {
          // No Stripe element needed; just re-prepare as an offline payment.
          await this.preparePayment({ offline: true })
          return
        }
        const info = await this.preparePayment(this.method === 'affirm' ? { affirm: true } : {})
        this.elements = this.stripe.elements({ clientSecret: info.client_secret, loader: 'always' })
        if (this.method === 'card') {
          await this.mountCard()
          this.mountWallets(info.client_secret)
        } else {
          await this.mountAffirm()
        }
      } catch (e) {
        // The old Elements group still points at the previous PaymentIntent —
        // drop it so a retry can't charge the stale amount.
        this.elements = null
        this.localError = e.message || errorMessage
      } finally {
        this.loading = false
      }
    },
    // Called by the parent's footer "Pay" button.
    async submit() {
      if (this.loading) return
      if (this.method !== 'offline' && !this.elements) {
        this.localError = 'Payment is not ready. Please go back and continue to payment again.'
        return
      }
      this.loading = true
      this.localError = ''

      const billing = {
        billing_details: {
          name: this.paymentInfo.customer.full_name,
          email: this.paymentInfo.customer.email,
        },
      }

      try {
        if (this.method === 'offline') {
          await this.finish()
        } else if (this.method === 'card') {
          const { error, paymentIntent } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: { return_url: this.paymentInfo.redirect_url, payment_method_data: billing },
            redirect: 'if_required',
          })
          if (error && error.message) { this.localError = error.message }
          else if (paymentIntent) { await this.finish() }
        } else if (this.method === 'affirm') {
          const { error } = await this.stripe.confirmAffirmPayment(this.paymentInfo.client_secret, {
            return_url: this.paymentInfo.redirect_url,
          })
          if (error && error.message) { this.localError = error.message }
          // Affirm always redirects; the return is handled on page mount.
        }
      } catch (e) {
        this.localError = e.message || 'Payment failed. Please try again.'
      } finally {
        this.loading = false
      }
    },
    async finish() {
      const result = await this.confirmPayment()
      this.$emit('done', result)
    },
  },
}
</script>

<style lang="scss">
.payment-block { max-width: 560px; }
.pay-error {
  background: #fdecea;
  border: 0.5px solid #f5c2bd;
  color: #922;
  border-radius: var(--radius-md);
  padding: 12px 16px;
  font-size: var(--fs-small);
  margin-bottom: 16px;
}
.payment-card {
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 24px 28px;
}
.pay-method-toggle {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.pay-method-toggle label {
  flex: 1;
  min-width: 160px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 0.5px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--fs-small);
  cursor: pointer;
}
.pay-method-toggle label.active {
  border-color: var(--brand);
  background: var(--brand-soft);
  color: var(--brand-deep);
}
.pay-note { font-size: var(--fs-small); color: var(--text-secondary); margin: 4px 0 0; line-height: 1.5; }
.pay-express-divider {
  display: flex;
  align-items: center;
  margin: 18px 0;
  font-size: var(--fs-small);
  color: var(--text-secondary);

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 0.5px solid var(--border);
  }

  span { padding: 0 12px; }
}
</style>
